import _ from 'lodash';
import datefns from 'date-fns';
import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').InsuranceContract} Contract
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetContractsActionOpts
 * @property {string} organization
 *
 * @callback GetContractsAction
 * @param {ActionContext} ctx
 * @param {GetContractsActionOpts} opts
 * @returns {Promise<Contract[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetContractsAction}
 */
export const getContracts = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (!opts.organization) throw new Error(`${nms}: Organization filter is required`);

  // fetch from sdk
  const query = {
    organization: opts.organization,
    $populate: {
      insurer: {
        service: 'organizations',
        method: 'get',
        localKey: 'insurer'
      },
      insurerCompany: {
        service: 'insurance-contracts',
        method: 'get',
        localKey: 'insurer'
      }
    }
  };
  const contracts = await sdk.insurance.contracts()
    .find(query)
    .then(({ items }) => items.map(({ $populated: { insurer, insurerCompany }, ...i }) => ({
      ...i,
      insurer: insurer || insurerCompany || { id: i.insurer }
    })));
  ctx.commit('updateInContracts', { items: contracts, upsert: true });

  return contracts;
};

/**
 * @typedef {Object} GetPatientContractActionOpts
 * @property {string} patient patient id
 * @property {number} date validity date
 *
 * @callback GetPatientContractAction
 * @param {ActionContext} ctx
 * @param {GetPatientContractActionOpts} opts
 * @returns {Promise<Contract>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetPatientContractAction}
 */
export const getPatientContract = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (!opts.patient) throw new Error(`${nms}: Patient filter is required`);
  if (!opts.date) throw new Error(`${nms}: Date filter is required`);
  opts.date = datefns.getTime(opts.date);

  // fetch from sdk
  const query = {
    type: { $in: [ 'insurance-patient', 'corporate-partner-patient' ] },
    insured: opts.patient,
    startAt: { $lte: opts.date },
    expiresAt: { $gte: opts.date },
    $populate: {
      service: {
        service: 'services',
        method: 'get',
        localKey: 'meta.service'
      }
    }
  };
  const contract = await sdk.insurance.contracts()
    .findOne(query)
    .then(item => item && { ..._.omit(item, '$populated'), ...item.$populated });
  // ctx.commit('updateInContracts', { items: [ contract ], upsert: true });

  return contract;
};

/**
 * TODO: flesh out parameters
 * @typedef {Object} CreateContractActionOpts
 *
 * @callback CreateContractAction
 * @param {ActionContext} ctx
 * @param {CreateContractActionOpts} opts
 * @returns {Promise<Contract>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CreateContractAction}
 */
export const createContract = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  const data = opts;
  if (_.isEmpty(data)) throw new Error(`${nms}: Invalid create op payload`);

  // fetch from sdk
  const contract = await sdk.insurance.contracts().create(data);
  ctx.commit('updateInContracts', { items: [ contract ], upsert: true });

  return contract;
};

/**
 * @typedef {Object} RemoveContractActionOpts
 * @property {string[]} contracts contract ids
 *
 * @callback RemoveContractAction
 * @param {ActionContext} ctx
 * @param {RemoveContractActionOpts} opts
 * @returns {Promise<Contract>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {RemoveContractAction}
 */
export const removeContracts = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (_.isEmpty(opts.contracts)) throw new Error(`${nms}: Contract ids required`);

  // fetch from sdk
  const contract = await sdk.insurance.contracts().remove({ id: { $in: opts.contracts } });
  ctx.commit('removeInContracts', { items: [ contract ] });

  return contract;
};
